import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Check, X, GraduationCap, FileText, BookOpen, ScrollText, FlaskConical, Sparkles } from "lucide-react";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { useStore } from "@/lib/store";
import { toast } from "sonner";

const EXAMPLE = "When an AED reports a red X, immediately contact the site manager. If nobody responds within four hours, notify me.";
const DOCS = ["AED Readiness SOP v4", "Field Service Playbook", "Compliance Checklist 2025", "Customer Onboarding Guide"];
const SOPS = ["Red-X escalation procedure", "Battery replacement workflow", "Site contact protocol"];

function ruleFrom(text) {
  return text.trim().replace(/\.$/, "");
}

export default function TrainingLab() {
  const { state, queueRule, approveRule, addActivity } = useStore();
  const [empId, setEmpId] = useState(state.employees[0]?.id || "ava");
  const emp = state.employees.find((e) => e.id === empId) || state.employees[0];
  const [messages, setMessages] = useState([{ from: "ai", text: `I'm ready to learn, Lewis. Tell me a rule for how you'd like me to work, in plain language.` }]);
  const [input, setInput] = useState("");
  const scroller = useRef(null);
  useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const teach = (text) => {
    const t = (text ?? input).trim(); if (!t) return;
    setMessages((m) => [...m, { from: "user", text: t }]);
    setInput("");
    const rule = ruleFrom(t);
    setTimeout(() => {
      setMessages((m) => [
        ...m,
        { from: "ai", text: `Here's my understanding: ${t}` },
        { from: "ai", text: `I'll turn that into an operating rule:\n"${rule}."` },
        { from: "ai", text: `Example — if AED CS-1048 shows a red X, I'll contact the site manager right away, and if there's no response in four hours I'll notify you. Shall I add this to my training?`, rule },
      ]);
    }, 550);
  };

  const proposeToQueue = (rule) => {
    const id = `rule-${Date.now()}`;
    queueRule({ id, employeeId: emp.id, text: rule });
    toast.info("Rule proposed", { description: "Awaiting your approval below." });
  };

  const approve = (id, text) => {
    approveRule(id);
    addActivity({ employee: emp.id, task: `Learned a new rule`, source: "AI Training Lab", action: `Rule approved: ${text}`, consulted: [], approval: "Granted", result: "Added to training history", icon: "GraduationCap" });
    toast.success(`${emp.name} learned a new rule`);
  };

  const pending = state.trainingQueue.filter((r) => r.employeeId === emp.id);

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-white sm:text-4xl">AI Training Lab</h1>
        <p className="mt-1 text-sm text-slate-400">Train associates conversationally — instructions become visible operating rules you approve</p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {state.employees.map((e) => (
          <button key={e.id} onClick={() => setEmpId(e.id)} className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${empId === e.id ? "border-[#00E5FF] bg-[#00E5FF]/10 text-white" : "border-white/10 text-slate-300"}`} data-testid={`train-select-${e.id}`}>
            <img src={e.avatar} alt="" className="h-5 w-5 rounded-full object-cover" /> {e.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* conversation */}
        <div className="glass flex h-[560px] flex-col rounded-2xl lg:col-span-2" data-testid="training-conversation">
          <div className="flex items-center gap-3 border-b border-white/8 p-4">
            <EmployeeAvatar employee={emp} size={44} showStatus={false} />
            <div><div className="font-display font-semibold text-white">{emp.name}</div><div className="text-xs text-slate-400">Training conversation</div></div>
            <button onClick={() => setMessages((m) => [...m, { from: "user", text: "Run a quick test." }, { from: "ai", text: `Test: I detected a red-X on CS-1048, contacted the site manager, and set a 4-hour timer to notify you if there's no response. Working as instructed.` }])} className="ml-auto flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-slate-200 hover:border-[#00E5FF]/40" data-testid="test-employee-btn"><FlaskConical size={14} /> Test Employee</button>
          </div>
          <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto p-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm ${m.from === "user" ? "bg-gradient-to-r from-[#00E5FF] to-[#39C0FF] text-[#030A16]" : "border border-white/10 bg-[#0A1326] text-slate-100"}`}>
                  {m.text}
                  {m.rule && (
                    <button onClick={() => proposeToQueue(m.rule)} className="mt-2 flex items-center gap-1.5 rounded-lg bg-[#00E5FF]/15 px-3 py-1.5 text-xs text-[#00E5FF]" data-testid="propose-rule-btn"><Sparkles size={13} /> Propose this rule for approval</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 px-4 pb-2"><button onClick={() => teach(EXAMPLE)} className="rounded-full border border-white/10 px-3 py-1 text-[11px] text-slate-300 hover:border-[#00E5FF]/40" data-testid="training-example">Use example instruction</button></div>
          <div className="flex items-center gap-2 border-t border-white/8 p-3">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && teach()} placeholder={`Teach ${emp.name} a rule…`} className="h-10 flex-1 rounded-full border border-white/10 bg-[#030A16]/60 px-4 text-sm text-white focus:border-[#00E5FF]/50" data-testid="training-input" />
            <button onClick={() => teach()} className="grid h-10 w-10 place-items-center rounded-full bg-[#00E5FF] text-[#030A16]" data-testid="training-send"><Send size={16} /></button>
          </div>
        </div>

        {/* side: rules & knowledge */}
        <div className="space-y-4">
          <div className="glass rounded-2xl p-4" data-testid="rules-awaiting">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-white"><GraduationCap size={15} className="text-[#FFBF00]" /> Rules awaiting approval</div>
            <AnimatePresence>
              {pending.length === 0 && <div className="text-xs text-slate-500">No rules waiting. Teach one on the left.</div>}
              {pending.map((r) => (
                <motion.div key={r.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }} className="mb-2 rounded-xl border border-[#FFBF00]/20 bg-[#FFBF00]/5 p-3">
                  <div className="text-sm text-slate-200">{r.text}</div>
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => approve(r.id, r.text)} className="flex items-center gap-1 rounded-lg bg-[#22D3EE] px-3 py-1 text-xs font-semibold text-[#030A16]" data-testid={`approve-rule-${r.id}`}><Check size={13} /> Approve</button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className="glass rounded-2xl p-4" data-testid="training-history">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-white"><ScrollText size={15} className="text-[#00E5FF]" /> Training history</div>
            <div className="space-y-2">
              {emp.trainingHistory.map((t, i) => <div key={i} className="rounded-lg border border-white/8 bg-[#030A16]/40 p-2.5"><div className="font-mono text-[11px] text-[#00E5FF]">{t.date}</div><div className="text-xs text-slate-300">{t.rule}</div></div>)}
            </div>
          </div>

          <div className="glass rounded-2xl p-4" data-testid="training-knowledge">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-white"><BookOpen size={15} className="text-[#8A2BE2]" /> Knowledge & documents</div>
            <div className="mb-2 text-[10px] font-mono uppercase text-slate-500">Business documents</div>
            <div className="mb-3 space-y-1.5">{DOCS.map((d) => <div key={d} className="flex items-center gap-2 rounded-lg border border-white/8 bg-[#030A16]/40 px-2.5 py-1.5 text-xs text-slate-300"><FileText size={13} className="text-slate-500" /> {d}</div>)}</div>
            <div className="mb-2 text-[10px] font-mono uppercase text-slate-500">Standard operating procedures</div>
            <div className="flex flex-wrap gap-1.5">{SOPS.map((s) => <span key={s} className="rounded-full border border-white/10 px-2 py-0.5 text-[11px] text-slate-300">{s}</span>)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
